import { isoZ, z } from 'my-zod'
import { enumOptionsToObject, ProductLine, Timezone } from '../../internal'
import { additionalQuestionResponse } from './additional-question-response'
import { dateFns, dateTimeFns, Iso } from 'iso-fns2'
import { getReservationVehiclesFromIntendedAssetFamilyIds } from './get-reservation-vehicles-from-intended-asset-family-ids'
import assert from 'assert'

const RESERVATION_EDIT_DURATION_IN_DAYS = 90

const ReservationCancellationReasonOptions = [
  'MistakenOrder',
  'UnexpectedConflict',
  'BadWeather',
  'OverbookedFleet',
  'UnplannedVehicleMaintenance',
  'CustomerReason',
  'CustomerDisputed',
  'ThirdParty'
] as const
const ReservationCancellationReasons = enumOptionsToObject(ReservationCancellationReasonOptions)

const reservationVehicleModel = z.object({
  shortId: z.string(),
  assetFamilyId: z.string(),
  id: z.string(),
  pricingOverride: z.number().int().nonnegative().nullable(),
  /**
   * not the time the row was inserted into the database, rather the time
   * the customer declared their intent to book (as represented by reservation vehicle create context).
   * This can be updated if a reservation vehicle needs to be re-quoted
   */
  quotedDate: isoZ.instant(),
  quotedDateLocal: isoZ.date(),
  quotedTimeLocal: isoZ.time(),
  numberOfUnitsAvailable: z.number().int().nonnegative()
})

const riderModificationStatusOptions = ['Locked', 'Unlocked'] as const
const riderModificationStatus = enumOptionsToObject(riderModificationStatusOptions)

const reservationModeOptions = ['arrival', 'mpwr'] as const
const reservationMode = enumOptionsToObject(reservationModeOptions)

const reservationModel = z.object({
  id: z.string(),
  shortId: z.string().min(1),
  outfitterId: z.number(),
  customerOrderId: z.string().nullable(),
  productId: z.string().nullable(),
  productVersionId: z.number().nullable(),
  waiverContentId: z.string(), // honestly it can be nullable, because migrated from rentalProduct (but in prod we almost don't have active products without waiverContentId)
  referringDealerId: z.number().nullable(),

  vehicles: reservationVehicleModel.array(),

  // beginDate and endDate will be the same for Experiences, but may be different for Pickup and Delivery.
  beginDate: isoZ.date(),
  endDate: isoZ.date().nullable(),

  // In the case where users select a time slot, this will be exactly equal to that time slot.
  // Null for Pickup and Delivery, always populated for TimeSlot and Hourly.
  ridingStartTime: isoZ.time().nullable(),
  ridingEndTime: isoZ.time().nullable(),

  insuranceCarrier: z.string().nullish(),
  insurancePolicyNumber: z.string().nullish(),
  towingVehicleLicensePlateNumber: z.string().nullish(),

  additionalQuestionResponses: additionalQuestionResponse.array(),
  riderModificationStatus: z.enum(riderModificationStatusOptions),
  reservationMode: z.enum(reservationModeOptions),

  cancellationDate: isoZ.instant().nullable(),
  cancellationReason: z.enum(ReservationCancellationReasonOptions).nullable(),
  canceledByProfileId: z.number().nullable()
})

const reservationVehicleSystemView = reservationVehicleModel.augment({
  reservationId: z.string(),
  assetFamilyName: z.string()
})

function isReservationInFutureOrToday(startDateTime: Iso.DateTime | null, outfitterTimezone: Timezone | null) {
  return (
    startDateTime &&
    dateFns.isEqualOrBefore(dateFns.now(outfitterTimezone || 'America/Chicago'), dateFns.from(startDateTime))
  )
}

function isReservationWithinCheckOutWindow(
  reservation: Pick<ReservationSchemas.reservationModel, 'beginDate' | 'endDate' | 'ridingStartTime'>,
  outfitterTimezone: Timezone | null | undefined
): boolean {
  if (!reservation.ridingStartTime || !reservation.endDate) return true

  const timezone = outfitterTimezone || 'US/Central'
  // Can checkout before begin date (16h) and after end date (6 days)
  const startDate = dateFns.toDateTime(reservation.beginDate, reservation.ridingStartTime)
  const isEarlyCheckOutAvailable = dateTimeFns.isEqualOrAfter(
    dateTimeFns.now(timezone),
    dateTimeFns.add(startDate, { hours: -24 })
  )

  const isBookingDayCheckOutAvailable = dateFns.isEqualOrAfter(dateFns.now(timezone), reservation.beginDate)

  return (
    (isEarlyCheckOutAvailable || isBookingDayCheckOutAvailable) &&
    dateFns.isEqualOrBefore(
      dateFns.now(timezone),
      dateFns.add(reservation.endDate, {
        days: 5
      })
    )
  )
}

function getAssetClassForVehicles<V extends { assetFamilyId: string }>(
  vehicles: V[],
  assetFamilies: { id: string; assetClassId: number | null; productLine: ProductLine | null }[]
): (V & { productLine: ProductLine | null; assetClassId: number | null })[] {
  const newVehicles = vehicles.map((v) => {
    const match = assetFamilies.find((a) => a.id === v.assetFamilyId)
    assert.ok(match, `No asset family found matching id "${v.assetFamilyId}"`)
    return { ...v, productLine: match.productLine, assetClassId: match.assetClassId }
  })
  return newVehicles
}

export const ReservationSchemas = {
  reservationModel,
  reservationVehicleModel,
  riderModificationStatus,
  riderModificationStatusOptions,
  reservationMode,
  reservationModeOptions,
  reservationVehicleSystemView,
  ReservationCancellationReasonOptions,
  ReservationCancellationReasons,
  RESERVATION_EDIT_DURATION_IN_DAYS,
  isReservationInFutureOrToday,
  getReservationVehiclesFromIntendedAssetFamilyIds,
  isReservationWithinCheckOutWindow,
  additionalQuestionResponse,
  getAssetClassForVehicles
}

export declare namespace ReservationSchemas {
  export type reservationModel = z.infer<typeof reservationModel>
  export type reservationVehicleModel = z.infer<typeof reservationVehicleModel>
  export type reservationVehicleSystemView = z.infer<typeof reservationVehicleSystemView>
  export type ReservationCancellationReason = (typeof ReservationCancellationReasonOptions)[number]
  export type RiderModificationStatus = (typeof riderModificationStatusOptions)[number]
  export type ReservationMode = (typeof reservationModeOptions)[number]
  export type additionalQuestionResponse = z.infer<typeof additionalQuestionResponse>
}
